/**
 * Why a file was rejected, as a modal the supplier has to read.
 *
 * The upload endpoint answers 201 whether or not the file validated — failure
 * lives in `is_success` and the exception rows — so a rejected file used to
 * raise a green "uploaded — 0 rows" toast and silently leave the month
 * outstanding. A toast is the wrong shape for this anyway: it disappears while
 * the supplier is still reading the column name they got wrong.
 */
import { AlertCircle, Download, FileSpreadsheet } from 'lucide-react';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { spreadsheetRow } from '../uploadCycle';
import type { UploadFailure } from './useMyObligations';

interface UploadErrorDialogProps {
  failure: UploadFailure | null;
  onClose: () => void;
  onDownloadTemplate: (failure: UploadFailure) => void;
}

/** Beyond this the list stops being read and starts being wallpaper. */
const PROBLEMS_SHOWN = 12;

/** "3 more to fix and 5 more ignored" — never "5 more problems" for warnings. */
function hiddenLabel(errors: number, warnings: number): string | null {
  const parts: string[] = [];
  if (errors > 0) parts.push(`${errors.toLocaleString()} more to fix`);
  if (warnings > 0) parts.push(`${warnings.toLocaleString()} more ignored`);
  return parts.length > 0 ? parts.join(' and ') : null;
}

export default function UploadErrorDialog({ failure, onClose, onDownloadTemplate }: UploadErrorDialogProps) {
  if (!failure) return null;

  const errors = failure.problems.filter((p) => p.severity === 'error');
  const warnings = failure.problems.filter((p) => p.severity !== 'error');
  const shown = [...errors, ...warnings].slice(0, PROBLEMS_SHOWN);
  // Counts cover the whole file; the list travels capped.
  const totalErrors = failure.counts?.error ?? errors.length;
  const totalWarnings = failure.counts?.warning ?? warnings.length;
  const hidden = hiddenLabel(
    totalErrors - shown.filter((p) => p.severity === 'error').length,
    totalWarnings - shown.filter((p) => p.severity !== 'error').length,
  );
  // What is true of the month now — not what is true of a month that never had
  // a file. A rejected replace leaves the file already on record untouched.
  const consequence = failure.hadFileAlready
    ? `the file already on record for ${failure.datasetLabel}, ${failure.periodLabel} is unchanged.`
    : `${failure.datasetLabel} for ${failure.periodLabel} is still outstanding.`;
  const rowCount =
    failure.rejectedRows && failure.totalRows
      ? `${failure.rejectedRows.toLocaleString()} of ${failure.totalRows.toLocaleString()} rows could not be read, and a month is only accepted when every row passes.`
      : null;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="upload-error-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700 dark:text-red-400">
            <AlertCircle className="h-5 w-5 shrink-0" />
            That file was not accepted
          </DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-1">
              <p>
                Nothing was saved, and <span className="font-medium text-foreground">{consequence}</span> Fix
                the points below and upload again.
              </p>
              {rowCount && <p>{rowCount}</p>}
              <p className="flex items-center gap-1.5 text-xs">
                <FileSpreadsheet className="h-3.5 w-3.5 shrink-0" />
                <span className="break-all">{failure.fileName}</span>
              </p>
            </div>
          </DialogDescription>
        </DialogHeader>

        {failure.problems.length === 0 ? (
          // The importer rejected it but recorded no reason. Saying "the file
          // could not be read" would be a guess, and the wrong one whenever the
          // server failed after reading it perfectly well.
          <p className="rounded-lg border border-red-200 bg-red-50/60 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
            The file was rejected, but no reason was recorded. Check it is a CSV or Excel file saved from the
            template and try again — if it happens again, send this file to your customer so they can have it
            looked at.
          </p>
        ) : (
          <div className="max-h-[45vh] space-y-2 overflow-y-auto pr-1">
            {shown.map((problem, i) => (
              <div
                key={`${problem.column_name}-${problem.row_number}-${i}`}
                className="flex items-start gap-2 rounded-lg border p-3 text-sm"
                data-testid="upload-problem"
              >
                <Badge
                  variant="outline"
                  className={
                    problem.severity === 'error'
                      ? 'shrink-0 border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300'
                      : 'shrink-0 border-yellow-200 bg-yellow-50 text-yellow-700 dark:border-yellow-900 dark:bg-yellow-950/30 dark:text-yellow-300'
                  }
                >
                  {problem.severity === 'error' ? 'Must fix' : 'Ignored'}
                </Badge>
                <div className="min-w-0">
                  <p className="text-foreground">{problem.message}</p>
                  {spreadsheetRow(problem.row_number) !== null && (
                    <p className="text-xs text-muted-foreground">
                      Row {spreadsheetRow(problem.row_number)?.toLocaleString()} in the file
                    </p>
                  )}
                </div>
              </div>
            ))}
            {hidden && <p className="px-1 text-sm text-muted-foreground">…and {hidden}.</p>}
          </div>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            variant="outline"
            onClick={() => onDownloadTemplate(failure)}
            data-testid="upload-error-template"
          >
            <Download className="mr-2 h-4 w-4" />
            Download the {failure.datasetLabel} template
          </Button>
          <Button onClick={onClose} data-testid="upload-error-close">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
